<template>
  <div>
    <div>
      <NavigationBreadcrumb :items="breadcrumb_items"></NavigationBreadcrumb>
      <h3 class="mt-3">
        {{ $getVisibleNames("mesh.capacity", false, "Capacidad") }}
        <AlternativeNameButton
          :table_name="'mesh.capacity'"
          :allows_crud="allows_crud"
        ></AlternativeNameButton>
      </h3>
      <div style="display: flex">
        <div class="col" style="text-align: left">
          <ButtonMedium
            v-can="'mesh.add_capacity'"
            v-if="allows_crud"
            :tooltip_text="
              $t('reusable.common.addTooltip', {
                entity: $getVisibleNames('mesh.capacity', false, 'Capacidad'),
              })
            "
            :text_button="$t('reusable.common.add')"
            :click_button="() => $bvModal.show(`new-capacity-modal`)"
            :icon="'plus'"
            :variant="'primary'"
            :custom_class="``"
          >
          </ButtonMedium>
        </div>
      </div>
      <GenericBTable
        :items="capacities"
        :fields="capacities_fields"
        :filter="input_search"
        :filterCustom="filterCustom"
        @emitChangeSearchField="changeInputSearch"
        :show_pagination="true"
        :search_filter="true"
        :columns_display="true"
        :allows_crud="allows_crud"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template #cell(description)="row">
          <RenderVHtml :content="row.item.description"></RenderVHtml>
        </template>
        <template #cell(faculties)="row">
          <div v-if="row.item.is_global">
            <template>
              <div>
                {{
                  $t("curricularDefinitions.capacities.allSelected", {
                    entity: $getVisibleNames(
                      "mesh.faculty",
                      true,
                      "Facultades"
                    ),
                  })
                }}
              </div>
            </template>
          </div>
          <template v-else>
            <div>
              <div
                v-for="item in getFacultiesList(row.item.faculties)"
                :key="item.id"
              >
                {{ item.name }}
              </div>
            </div>
          </template>
        </template>
        <template v-if="allows_crud" #cell(actions)="row">
          <ButtonSmall
            v-can="'mesh.change_capacity'"
            :tooltip_text="
              $t('reusable.common.editTooltip', {
                entity: $getVisibleNames('mesh.capacity', false, 'Capacidad'),
              })
            "
            :click_button="
              () => $bvModal.show(`edit-capacity-modal-${row.item.id}`)
            "
            :custom_class="``"
            :icon="'square'"
          ></ButtonSmall>
          <ButtonSmall
            v-can="'mesh.delete_capacity'"
            :click_button="() => askForDeleteCapacity(row.item.id)"
            :tooltip_text="
              $t('reusable.common.deleteTooltip', {
                entity: $getVisibleNames('mesh.capacity', false, 'Capacidad'),
              })
            "
            :icon="'trash'"
          ></ButtonSmall>
          <b-modal
            :id="`edit-capacity-modal-${row.item.id}`"
            :title="
              $t('reusable.common.editModalTitle', {
                entity: $getVisibleNames('mesh.capacity', false, 'Capacidad'),
              })
            "
            size="lg"
            hide-footer
          >
            <CapacityForm
              :Capacity="row.item"
              @updated="slotUpdatedCapacity"
              :show_title="false"
            ></CapacityForm>
          </b-modal>
        </template>
      </GenericBTable>
    </div>
    <b-modal
      :id="`new-capacity-modal`"
      :hide-footer="true"
      :title="
        $t('reusable.common.createModalTitle', {
          entity: $getVisibleNames('mesh.capacity', false, 'Capacidad'),
        })
      "
      size="lg"
    >
      <CapacityForm @created="slotCreatedCapacity"></CapacityForm>
    </b-modal>
  </div>
</template>
<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "CapacitiesView",
  components: {
    CapacityForm: () => import("@/components/mesh/Capacities/CapacityForm"),
    GenericBTable: () => import("@/components/reusable/GenericBTable"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
  },
  props: {},
  data() {
    return {
      sortBy: "",
      input_search: "",
      sortDesc: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      faculties: names.FACULTIES,
      capacities: names.CAPACITIES,
    }),
    breadcrumb_items() {
      return [
        {
          text: this.$t("reusable.breadcrumb.home"),
          to: "/home",
        },
        {
          text: this.$getVisibleNames("mesh.capacity", false, "Capacidad"),
        },
      ];
    },
    allows_crud() {
      const has_permission = this.$hasPermissions("mesh.add_capacity");
      if (
        has_permission &&
        this.user &&
        (this.user.group_names.includes(
          "DIRECTIVOS ACADÉMICOS INSTITUCIONALES"
        ) ||
          this.user.is_superuser)
      )
        return has_permission;
      else return false;
    },
    capacities_fields() {
      return [
        {
          key: "name",
          label: String(
            this.$getVisibleNames("mesh.capacity", true, "Capacidades")
          ),
          sortable: true,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "description",
          label: this.$t("reusable.common.description"),
          sortable: true,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "faculties",
          label: this.$getVisibleNames("mesh.faculty", true, "Facultades"),
          sortable: true,
          // La celda muestra los nombres de las facultades, no sus ids
          sortByFormatted: (value, key, item) =>
            item.is_global
              ? ""
              : this.getFacultiesList(item.faculties)
                  .map((x) => x.name)
                  .join(", "),
          thStyle: {
            width: "20%",
          },
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "actions",
          label: "",
          display_column: true,
          thClass: "noprint",
          tdClass: "noprint",
        },
      ];
    },
  },
  methods: {
    filterCustom(row) {
      if (
        this.$filtered(String(row.name), this.input_search) ||
        this.$filtered(String(row.description), this.input_search)
      ) {
        return true;
      } else {
        false;
      }
    },
    changeInputSearch(input_search) {
      this.input_search = input_search;
    },
    getFacultiesList(faculties) {
      if (faculties.length > 0) {
        const list = this.faculties.filter((element) =>
          faculties.includes(element.id)
        );
        return list.sort((a, b) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        });
      } else return [];
    },
    askForDeleteCapacity(capacity_id) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle", {
          entity: this.$getVisibleNames("mesh.capacity", false, "Capacidad"),
        }),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch(names.DELETE_CAPACITY, capacity_id).then(() => {
            toast(
              this.$getVisibleNames("mesh.capacity", false, "Capacidad") +
                " " +
                this.$t("reusable.common.deleted")
            );
          });
        }
      });
    },
    slotCreatedCapacity() {
      this.$bvModal.hide("new-capacity-modal");
    },
    slotUpdatedCapacity(capacity) {
      this.$bvModal.hide(`edit-capacity-modal-${capacity.id}`);
    },
  },
  mounted() {
    this.$store.dispatch(names.FETCH_FACULTIES);
    this.$store.dispatch(names.FETCH_CAPACITIES);
  },
  created() {},
};
</script>

<style scoped>
th {
  background-color: var(--primary-color);
  border-radius: 6px;
  box-shadow: 1px 1px 3px rgb(72, 72, 72);
}

.title-header {
  padding: 0;
  margin-top: auto;
}
.search-capacity {
  max-width: 50rem;
  margin-left: auto;
  margin-right: 18.5%;
  margin-bottom: 1rem;
  margin-top: 0.5rem;
}
.edit-section {
  margin-top: -2%;
}
</style>